import React from 'react'
import { Message, Whisper, Tooltip } from 'rsuite'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import CustomIcons from '../../../ui_reusable/CustomIcons'

import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import { GridCellNormalText } from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { EasyIconButton } from '../../../ui_reusable/styles/Button.styles'
import { StandardToggleButton } from '../../../ui_reusable/styles/ToggleButton.styles'
import { StandardCheckbox } from '../../../ui_reusable/StandardInputs'
import { ToggleContainer } from './styles/ProjectSolutionList.styles'
import theme from '../../../../styles/theme'

const DisabledTooltipWrapper = ({ children, isDisabled, tooltipText }) => {
  if (!isDisabled) {
    return children
  }

  return (
    <Whisper
      placement="topStart"
      trigger="hover"
      speaker={
        <Tooltip
          style={{
            backgroundColor: theme.colors.background,
            color: theme.colors.veryVeryFadedText,
            outline: '1px solid',
          }}
        >
          {tooltipText}
        </Tooltip>
      }
    >
      <span>{children}</span>
    </Whisper>
  )
}

const ProjectSolutionList = ({ projectId, disabled = false }) => {
  const [solutions, setSolutions] = useState([])
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [projectSolutions, setProjectSolutions] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const id = projectId
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { pushToast } = useStandardToaster()

  useEffect(() => {
    const abortController = new AbortController()

    const getSolutions = async () => {
      setLoading(true) // Set loading to true when fetching starts
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/solutions?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const res = await response.json()

        const list = Array.isArray(res.data) ? res.data : res.data?.data
        setSolutions(Array.isArray(list) ? list : [])
        setTotal(res.total ?? res.data?.total ?? list?.length ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch solutions:', error)
          setSolutions([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false) // Set loading to false when done
        }
      }
    }

    getSolutions()

    return () => {
      abortController.abort()
    }
  }, [page])

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  useEffect(() => {
    const getProjectSolutions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/${id}/solutions/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )
        const res = await response.json()

        const list = Array.isArray(res.data) ? res.data : res.data?.data
        setProjectSolutions(Array.isArray(list) ? list : [])
      } catch (error) {
        console.error('Failed to fetch project solutions:', error)
        pushToast(
          <Message type="error">Failed to load project solutions.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      }
    }

    if (id) {
      getProjectSolutions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh])

  const handleToggleProjectSolution = async (solutionId, status) => {
    if (disabled) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${solutionId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          method: 'PATCH',
          body: JSON.stringify({ is_active: status }),
        },
      )

      if (response.status === 200) {
        setRefresh(new Date().getTime())
        pushToast(
          <Message type="success">
            Solution access updated successfully.
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        pushToast(
          <Message type="error">Failed to update solution access.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } catch (error) {
      console.error('Failed to toggle project solution:', error)
      pushToast(
        <Message type="error">Failed to update solution access.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleCreateProjectSolution = async (solutionId) => {
    if (disabled) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          method: 'POST',
          body: JSON.stringify({ solution_id: solutionId }),
        },
      )

      if (response.status === 201) {
        setRefresh(new Date().getTime())
        pushToast(
          <Message type="success">
            Solution added to project successfully.
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        pushToast(
          <Message type="error">Failed to add project solution.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } catch (error) {
      console.error('Failed to create project solution:', error)
      pushToast(
        <Message type="error">Failed to add project solution.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleCheckboxChange = async (rowId, featureKey) => {
    const projectSolution = (projectSolutions ?? []).find(
      (elem) => elem.solution?.id === rowId,
    )

    if (!projectSolution) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/solutions/${rowId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          method: 'PATCH',
          body: JSON.stringify({
            features: {
              ...(projectSolution.features ?? {}),
              [featureKey]: !(projectSolution.features ?? {})[featureKey],
            },
          }),
        },
      )

      if (response.status === 200) {
        setRefresh(new Date().getTime())
        pushToast(
          <Message type="success">
            Solution features updated successfully.
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        pushToast(
          <Message type="error">Failed to update solution features.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } catch (error) {
      console.error('Failed to update feature:', error)
      pushToast(
        <Message type="error">Failed to update solution features.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleManage = (id, solutionId) => {
    navigate('/projects/' + id + '/solutions/' + solutionId)
  }

  // Grid configuration
  const headers = [
    {
      title: '',
      key: 'is_active',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Variables',
      key: 'variables',
      type: 'number',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Models',
      key: 'models',
      type: 'number',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Saved',
      key: 'saved',
      type: 'number',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Scenario',
      key: 'scenario_access',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      center: true,
      borderLeft: true,
    },
    {
      title: 'AI+',
      key: 'ai',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      center: true,
      borderLeft: true,
    },
    {
      title: 'KPI+',
      key: 'kpi',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Segment+',
      key: 'segment',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Geo+',
      key: 'geo',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Report+',
      key: 'report',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,

      borderLeft: true,
    },
    {
      //title: 'View / History / Configure',
      title: 'Configure',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      borderLeft: true,
      center: false,
    },
  ]

  return (
    <>
      <VersatileGrid
        isSearchable={false}
        headers={headers}
        data={solutions ?? []}
        gridContainerStyle={{
          gridTemplateColumns:
            '6fr 1.5fr 1.5fr 1.5fr 2.4fr 1fr 1fr 1fr 1fr 1fr 4fr',
        }}
        renderRow={(rowData, rowIdx) => {
          const safeProjectSolutions = projectSolutions ?? []
          const solutionExists = rowData.is_active
          const projectSolutionListExists = safeProjectSolutions.length > 0
          const hasProjectSolution = safeProjectSolutions.find(
            (e) => e.solution?.id === rowData.id && !e.is_active,
          )
          const buttonsShouldBeDisbled =
            !solutionExists ||
            !projectSolutionListExists ||
            !!hasProjectSolution

          return [
            <ToggleContainer>
              <DisabledTooltipWrapper
                isDisabled={disabled}
                tooltipText="Reactivate the project before changing solution access."
              >
                <StandardToggleButton
                  color="green"
                  disabled={disabled || !rowData.is_active}
                  checked={
                    !!safeProjectSolutions.find(
                      (e) => e.solution?.id === rowData.id && e.is_active,
                    )
                  }
                  onChange={async () => {
                    if (disabled) return

                    const projectSolution = safeProjectSolutions.find(
                      (e) => e.solution?.id === rowData.id,
                    )

                    if (projectSolution) {
                      await handleToggleProjectSolution(
                        projectSolution.solution.id,
                        !projectSolution.is_active,
                      )
                    } else {
                      await handleCreateProjectSolution(rowData.id)
                    }
                  }}
                >
                  {rowData.name}
                </StandardToggleButton>
              </DisabledTooltipWrapper>
            </ToggleContainer>,
            <GridCellNormalText>{rowData.variables}</GridCellNormalText>,
            <GridCellNormalText>{rowData.models}</GridCellNormalText>,
            <GridCellNormalText>{rowData.saved}</GridCellNormalText>,
            <DisabledTooltipWrapper
              isDisabled={true}
              tooltipText="This feature has been disabled"
            >
              <StandardToggleButton
                disabled
                checked={
                  !!safeProjectSolutions.find(
                    (e) =>
                      e.solution?.id === rowData.id &&
                      e.features?.scenario_access,
                  )
                }
                onChange={() =>
                  handleCheckboxChange(rowData.id, 'scenario_access')
                }
              />
            </DisabledTooltipWrapper>,
            <StandardCheckbox
              key={`${rowData.id}-ai-${!!safeProjectSolutions.find((e) => e.solution?.id === rowData.id && e.features?.ai)}`}
              disabled={buttonsShouldBeDisbled}
              checked={
                !!safeProjectSolutions.find(
                  (e) => e.solution?.id === rowData.id && e.features?.ai,
                )
              }
              onChange={() => handleCheckboxChange(rowData.id, 'ai')}
            />,
            <DisabledTooltipWrapper
              isDisabled={true}
              tooltipText="This feature has been disabled"
            >
              <StandardCheckbox
                disabled={true}
                checked={
                  !!safeProjectSolutions.find(
                    (e) => e.solution?.id === rowData.id && e.features?.kpi,
                  )
                }
                onChange={() => handleCheckboxChange(rowData.id, 'kpi')}
              />
            </DisabledTooltipWrapper>,
            <DisabledTooltipWrapper
              isDisabled={true}
              tooltipText="This feature has been disabled"
            >
              <StandardCheckbox
                disabled={true}
                checked={
                  !!safeProjectSolutions.find(
                    (e) => e.solution?.id === rowData.id && e.features?.segment,
                  )
                }
                onChange={() => handleCheckboxChange(rowData.id, 'segment')}
              />
            </DisabledTooltipWrapper>,
            <DisabledTooltipWrapper
              isDisabled={true}
              tooltipText="This feature has been disabled"
            >
              <StandardCheckbox
                disabled={true}
                checked={
                  !!safeProjectSolutions.find(
                    (e) => e.solution?.id === rowData.id && e.features?.geo,
                  )
                }
                onChange={() => handleCheckboxChange(rowData.id, 'geo')}
              />
            </DisabledTooltipWrapper>,
            <DisabledTooltipWrapper
              isDisabled={true}
              tooltipText="This feature has been disabled"
            >
              <StandardCheckbox
                disabled={true}
                checked={
                  !!safeProjectSolutions.find(
                    (e) => e.solution?.id === rowData.id && e.features?.report,
                  )
                }
                onChange={() => handleCheckboxChange(rowData.id, 'report')}
              />
            </DisabledTooltipWrapper>,
            <div style={{ display: 'flex' }}>
              {/*
                        <EasyIconButton>
                            <CustomIcons.EyeIcon
                                color={theme.colors.veryFadedText}
                                size="xs"
                                disabled={buttonsShouldBeDisbled}
                            />
                        </EasyIconButton>
                        <EasyIconButton>
                            <CustomIcons.ProcessIcon
                                size="xs"
                                color={theme.colors.veryFadedText}
                                disabled={buttonsShouldBeDisbled}
                            />
                        </EasyIconButton>
                         */}
              <EasyIconButton
                data-testid={`manage-${rowData.id}`}
                onClick={() => handleManage(id, rowData.id)}
                disabled={buttonsShouldBeDisbled}
              >
                <CustomIcons.PenIcon
                  color={theme.colors.veryFadedText}
                  size="xs"
                  disabled={buttonsShouldBeDisbled}
                />
              </EasyIconButton>
            </div>,
          ]
        }}
        maxDisplayedItems={10}
        serverSide
        serverTotal={total}
        serverPage={page}
        onPageChange={handlePageChange}
        loading={loading}
        showHeader={false}
        loadingMessage={'Loading solutions...'}
      />
    </>
  )
}

export default ProjectSolutionList
